/**
 * designBandOverlay.js — dual-color scene encoding (validated against Plotly 3.0.3)
 *
 * Each scene wedge shows TWO judgments at once:
 *   - inner CORE  = execution score  → this is Plotly's own wedge fill (we leave
 *                                      a hole in the overlay so it shows through)
 *   - BORDER all around = design score → a thin uniform frame we paint on top
 *
 * Plus heavy black separator rings at every level boundary (movie|act,
 * act|sequence, sequence|scene, and the outer edge of the scene ring).
 *
 * Hard requirements honored:
 *   - NON-INTERACTIVE: every element gets inline `pointer-events:none`, so all
 *     clicks/hovers (incl. the double-click-to-zoom on acts/sequences) pass
 *     straight through to Plotly's wedge paths. Inline style is used (not just
 *     CSS) so it holds regardless of stylesheet load order.
 *   - RE-ALIGNS on every Plotly redraw (caller re-invokes on plotly_afterplot).
 *   - Pure visual overlay: only appends into its own <g>, mutates no Plotly node.
 *
 * Matching: slices are matched to nodes by id read from `slice.__data__` (the
 * D3-bound hierarchy node), NOT by DOM index — Plotly renders slices
 * breadth-first (all acts, then sequences, then scenes), so index != data order.
 * `slice.__data__` also exposes `depth` (Movie=0, act=1, sequence=2, scene=3).
 */

import { gradeColors } from './Constants';

const OVERLAY_CLASS = 'design-band-overlay';
const SVG_NS = 'http://www.w3.org/2000/svg';

// ── Frame sizing: ONE scale-invariant rule for any node (scene/sequence/act).
// Two independent measurements drive everything:
//   t = radial thickness ("height", constant per ring, grows on zoom)
//   w = angular arc width ("width", varies per scene, can be a sliver)
//
//   Above/below bands (inner+outer): bR = clamp(BAND_FRAC·t, BAND_MIN, BAND_MAX)
//       → height-driven; identical for every scene in a ring; bigger when zoomed.
//   Left/right strips (sides):       bA = clamp((w − CORE_MIN)/2, SIDE_MIN, bR)
//       → width-driven; wants to equal bR (uniform frame); capped at bR so sides
//         are never thicker than top/bottom; floored at SIDE_MIN so even a thin
//         scene keeps a sliver of design on its sides; reserves CORE_MIN for the
//         execution core. On a sub-(2·SIDE_MIN)px scene the core yields and the
//         scene reads as solid design (design is the signal we most want kept).
const BAND_FRAC = 0.13; // above/below thickness as a fraction of height (t)
const BAND_MIN  = 5;    // px
const BAND_MAX  = 16;   // px
const SIDE_MIN  = 2;    // px — side strips never go below this
const CORE_MIN  = 4;    // px — angular core reserved before the sides yield

// Scene divider: thinner than Plotly's level (act/sequence) dividers, which are
// width 2. Scenes are the deepest level, so their dividers must be the thinnest.
const SCENE_DIVIDER_PX = 1.5;
const RING_STROKE_PX = 5; // heavy black level-separator rings
// Scene wedges narrower than this (arc length at mid-radius, px) get their
// label HIDDEN: Plotly renders scene labels at a fixed 12px font, rotates
// them radially on thin wedges, and centers them on the wedge's visual
// center — which in an annulus is biased outward — so on slivers the tail
// crosses the outer arc and the separator ring paints over it ("sc 7" → a
// mangled stub under the border). A wedge that can't legibly hold its label
// shows none; hover and selection still identify the scene. Re-evaluated on
// every redraw, so zooming in brings labels back when there's room.
const MIN_LABEL_ARC_PX = 16;
const HIDDEN_LABEL_CLASS = 'dbo-hidden-label';
// Selection focus ring — a color OUTSIDE the red→green score ramp so it never
// reads as a score. Blue with a white halo so it pops on both red and green.
const SELECT_COLOR = '#1f6bff';
const SELECT_HALO  = '#ffffff';
const SELECT_PX    = 3;

/**
 * Public entry. Wrapped so it can NEVER throw out of a Plotly event handler
 * (an exception inside plotly_afterplot would abort Plotly's event dispatch and
 * break interactions like double-click-to-zoom).
 *
 * @param {HTMLElement} chartEl   the Plotly graph div
 * @param {Array} sceneNodes      entries: { id, nodeType, design_score } (others ignored)
 * @param {string} [selectedId]   id of the currently selected node, to focus-ring
 */
export function renderDesignBands(chartEl, sceneNodes, selectedId) {
  try {
    _render(chartEl, sceneNodes, selectedId);
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('designBandOverlay skipped:', e);
  }
}

function _render(chartEl, sceneNodes, selectedId) {
  if (!chartEl) return;
  const root = chartEl.querySelector('g.sunburst');
  if (!root) return; // chart not drawn yet

  let overlay = root.querySelector(`.${OVERLAY_CLASS}`);
  if (overlay) {
    while (overlay.firstChild) overlay.removeChild(overlay.firstChild); // idempotent wipe
  } else {
    overlay = document.createElementNS(SVG_NS, 'g');
    overlay.setAttribute('class', OVERLAY_CLASS);
    overlay.style.pointerEvents = 'none';
    root.appendChild(overlay);
  }
  overlay.style.pointerEvents = 'none';

  // Restore labels hidden on a previous pass (min-arc rule below) BEFORE the
  // early return, so labels come back when scenes are toggled off/zoomed.
  root.querySelectorAll(`.${HIDDEN_LABEL_CLASS}`).forEach(el => {
    el.style.visibility = '';
    el.classList.remove(HIDDEN_LABEL_CLASS);
  });

  const slices = root.querySelectorAll('g.slice');
  if (slices.length === 0) return; // e.g. includeScenes toggled off

  // id -> design score, scenes only; plus the set of ALL scene ids (the
  // min-arc label rule applies to every scene wedge, framed or not).
  const designById = Object.create(null);
  const sceneIds = Object.create(null);
  (sceneNodes || []).forEach(n => {
    if (!n || !n.id || n.nodeType !== 'scene') return;
    sceneIds[n.id] = true;
    if (n.design_score != null) designById[n.id] = n.design_score;
  });

  // Collect every distinct ring-boundary radius (depth-agnostic, so it stays
  // correct when zoomed — __data__.depth is relative to the zoom root, radii
  // are not). Each unique inner/outer radius becomes a separator ring.
  let center = null, selGeom = null;
  const radii = [];
  const addRadius = r => {
    if (r == null || !isFinite(r)) return;
    for (const x of radii) if (Math.abs(x - r) < 1.5) return; // cluster within 1.5px
    radii.push(r);
  };

  slices.forEach(slice => {
    const dd = slice.__data__;
    const id = dd && ((dd.data && dd.data.id) || dd.id);
    const path = slice.querySelector('path.surface') || slice.querySelector('path');
    if (!path) return;
    const g = parseSliceGeometry(path.getAttribute('d'));
    if (!g) return;

    if (!center) center = { cx: g.cx, cy: g.cy };
    addRadius(g.ri);
    addRadius(g.ro);
    if (selectedId != null && id === selectedId) selGeom = g; // remember for focus ring

    // Min-arc label rule: hide labels on scene wedges too thin to hold them.
    if (id != null && sceneIds[id]) {
      const arcMid = Math.abs(angleSpan(g)) * (g.ri + g.ro) / 2;
      if (arcMid < MIN_LABEL_ARC_PX) {
        const st = slice.querySelector('g.slicetext');
        if (st) {
          st.style.visibility = 'hidden';
          st.classList.add(HIDDEN_LABEL_CLASS);
        }
      }
    }

    // Design frame for scenes with a score.
    const design = id != null ? designById[id] : undefined;
    if (design == null) return;
    // skip degenerate slivers / the center node
    if (Math.abs(angleSpan(g)) < 0.005 && g.laf === 0) return;

    // Scale-invariant border, relative to the node's own dimensions.
    const t = g.ro - g.ri;                          // radial thickness ("height")
    const w = Math.abs(angleSpan(g)) * g.ro;        // angular arc width ("width")
    // The center is always bigger than its borders: cap each border at ¼ of the
    // wedge's dimension ⇒ center ≥ ½ ⇒ center is at least 2× each border. This
    // also stops narrow scenes from being swallowed by their own frame.
    const bR = Math.min(clamp(t * BAND_FRAC, BAND_MIN, BAND_MAX), t / 4); // above/below
    const bA = Math.min(clamp((w - CORE_MIN) / 2, SIDE_MIN, bR), w / 4);  // left/right

    const frameD = buildFrameWithHole(g, bR, bA);
    // Site grade color, flattened to opaque so the SAME score is ALWAYS the same
    // color (a translucent band would blend with whatever execution color sits
    // under it, which is why equal scores looked different before).
    appendPath(overlay, frameD, colorForDesignScore(design), 'none', 'evenodd');
    // ONE thin black outline of the wedge perimeter (no core hole) — the scene
    // divider; thinner than the level (sequence/act) dividers.
    appendPath(overlay, sector(g, g.ri, g.ro), 'none', '#000', null, SCENE_DIVIDER_PX);
  });

  // Heavy black separator rings at every level boundary (drawn last, on top).
  if (center && radii.length) {
    radii.sort((a, b) => a - b);
    const maxR = radii[radii.length - 1];
    radii.forEach(r => {
      // inset the outermost ring by half the stroke so it isn't clipped at the edge
      const rr = r === maxR ? r - RING_STROKE_PX / 2 : r;
      appendRing(overlay, center.cx, center.cy, rr, RING_STROKE_PX);
    });
  }

  // Selection focus ring around the selected wedge (any level): white halo +
  // a blue ring drawn on the wedge's outer/inner arc edges. Blue sits outside
  // the red→green ramp so it never reads as a score.
  if (selGeom) {
    const outline = sector(selGeom, selGeom.ri, selGeom.ro);
    appendPath(overlay, outline, 'none', SELECT_HALO, null, SELECT_PX + 3);
    appendPath(overlay, outline, 'none', SELECT_COLOR, null, SELECT_PX);
  }
}

/* ───────────────────────── helpers ───────────────────────── */

function appendPath(overlay, d, fill, stroke, fillRule, strokeWidth) {
  const p = document.createElementNS(SVG_NS, 'path');
  p.setAttribute('d', d);
  p.setAttribute('fill', fill);
  p.setAttribute('stroke', stroke || 'none');
  if (stroke && stroke !== 'none') {
    p.setAttribute('stroke-width', strokeWidth != null ? strokeWidth : 1);
    p.setAttribute('stroke-linejoin', 'round');
    p.setAttribute('vector-effect', 'non-scaling-stroke');
  }
  if (fillRule) p.setAttribute('fill-rule', fillRule);
  p.setAttribute('shape-rendering', 'geometricPrecision');
  p.style.pointerEvents = 'none'; // inline — clicks must reach Plotly's wedge
  overlay.appendChild(p);
}

function appendRing(overlay, cx, cy, r, w) {
  const c = document.createElementNS(SVG_NS, 'circle');
  c.setAttribute('cx', cx); c.setAttribute('cy', cy); c.setAttribute('r', r);
  c.setAttribute('fill', 'none');
  c.setAttribute('stroke', '#000');
  c.setAttribute('stroke-width', w);
  c.style.pointerEvents = 'none';
  overlay.appendChild(c);
}

// Parse a Plotly 3.x sunburst slice path d into wedge geometry.
// Format: M innerStart L outerStart A ro,ro 0 laf sf outerEnd L innerEnd A ri,ri ... Z
function parseSliceGeometry(d) {
  if (!d) return null;
  const re = /([MLAZ])|(-?\d*\.?\d+(?:e-?\d+)?)/gi;
  const toks = [];
  let m;
  while ((m = re.exec(d))) toks.push(m[1] ? m[1].toUpperCase() : parseFloat(m[2]));
  const cmds = [];
  let i = 0;
  while (i < toks.length) {
    const c = toks[i++];
    if (c === 'M' || c === 'L') cmds.push({ c, pts: [toks[i++], toks[i++]] });
    else if (c === 'A') cmds.push({ c, pts: [toks[i++], toks[i++], toks[i++], toks[i++], toks[i++], toks[i++], toks[i++]] });
    else if (c === 'Z') cmds.push({ c });
  }
  const M = cmds.find(x => x.c === 'M');
  const arcs = cmds.filter(x => x.c === 'A');
  const lines = cmds.filter(x => x.c === 'L');
  if (!M || arcs.length < 2 || lines.length < 1) return null;
  const innerStart = { x: M.pts[0], y: M.pts[1] };
  const outerStart = { x: lines[0].pts[0], y: lines[0].pts[1] };
  const oArc = arcs[0];
  const ro = oArc.pts[0], laf = oArc.pts[3], sf = oArc.pts[4];
  const outerEnd = { x: oArc.pts[5], y: oArc.pts[6] };
  const ri = arcs[1].pts[0];
  if (!(ro > 0) || !(ri >= 0) || ro <= ri) return null;
  const dx = innerStart.x - outerStart.x, dy = innerStart.y - outerStart.y;
  const seg = Math.hypot(dx, dy);
  if (seg < 1e-6) return null;
  const ux = dx / seg, uy = dy / seg;
  return { cx: innerStart.x + ux * ri, cy: innerStart.y + uy * ri, ro, ri, outerStart, outerEnd, laf, sf };
}

// full-angle annular sector of this wedge between radii rA..rB
function sector(g, rA, rB) {
  const sc = (P, r) => ({ x: g.cx + (P.x - g.cx) * (r / g.ro), y: g.cy + (P.y - g.cy) * (r / g.ro) });
  const As = sc(g.outerStart, rB), Ae = sc(g.outerEnd, rB), Be = sc(g.outerEnd, rA), Bs = sc(g.outerStart, rA);
  const sfIn = g.sf ? 0 : 1;
  return `M${As.x},${As.y} A${rB},${rB} 0 ${g.laf} ${g.sf} ${Ae.x},${Ae.y} `
       + `L${Be.x},${Be.y} A${rA},${rA} 0 ${g.laf} ${sfIn} ${Bs.x},${Bs.y} Z`;
}

// vector helpers for the angularly-inset core
const _sub = (p, c) => ({ x: p.x - c.x, y: p.y - c.y });
const _norm = v => { const m = Math.hypot(v.x, v.y) || 1; return { x: v.x / m, y: v.y / m }; };
const _rot = (v, a) => ({ x: v.x * Math.cos(a) - v.y * Math.sin(a), y: v.x * Math.sin(a) + v.y * Math.cos(a) });
const _sang = (a, b) => Math.atan2(a.x * b.y - a.y * b.x, a.x * b.x + a.y * b.y);
function angleSpan(g) {
  const c = { x: g.cx, y: g.cy };
  return _sang(_norm(_sub(g.outerStart, c)), _norm(_sub(g.outerEnd, c)));
}
function sectorV(c, rA, rB, vS, vE, sf, angWidth) {
  const P = (v, r) => `${c.x + v.x * r},${c.y + v.y * r}`;
  const laf = Math.abs(angWidth) > Math.PI ? 1 : 0;
  return `M${P(vS, rB)} A${rB},${rB} 0 ${laf} ${sf} ${P(vE, rB)} `
       + `L${P(vE, rA)} A${rA},${rA} 0 ${laf} ${sf ? 0 : 1} ${P(vS, rA)} Z`;
}

// design frame = full wedge MINUS an inset core (uniform border on all 4 sides),
// expressed as one path with an even-odd hole so Plotly's execution fill shows
// through the core.
// Frame = full wedge minus an inset core. bR = radial inset (each of inner/outer
// band), bA = angular inset in px (each side strip; 0 ⇒ no side strips). Even-odd
// hole so the execution fill shows through the core.
function buildFrameWithHole(g, bR, bA) {
  // radial inset, kept under half the thickness so a core always survives radially
  const radial = Math.min(bR, (g.ro - g.ri) / 2 - 0.5);
  const coreRi = g.ri + radial, coreRo = g.ro - radial;
  const outer = sector(g, g.ri, g.ro);
  if (coreRo <= coreRi) return outer; // too thin radially → solid design wedge
  const c = { x: g.cx, y: g.cy };
  const vS = _norm(_sub(g.outerStart, c)), vE = _norm(_sub(g.outerEnd, c));
  const ang = _sang(vS, vE), dir = Math.sign(ang) || 1;
  const dth = bA / coreRo;                    // side strip as an angle
  if (2 * dth >= Math.abs(ang) - 1e-4) return outer; // no angular room → solid design
  const vS2 = _rot(vS, dir * dth), vE2 = _rot(vE, -dir * dth);
  const core = sectorV(c, coreRi, coreRo, vS2, vE2, g.sf, ang - 2 * dir * dth);
  return outer + ' ' + core;
}

// Use the site's canonical grade palette (gradeColors === the .grade1–.grade10
// CSS classes), keyed by the rounded design score, but FLATTENED over white to
// an opaque rgb. Flattening matches how those translucent grades already look
// on the white page, while guaranteeing the band can't pick up the execution
// color beneath it — so the same score is always the same color.
function colorForDesignScore(score) {
  const key = clamp(Math.round(score), 1, 10);
  return flattenOverWhite(gradeColors[key]);
}

function flattenOverWhite(rgba) {
  const m = /rgba?\(([^)]+)\)/i.exec(rgba || '');
  if (!m) return rgba;
  const p = m[1].split(',').map(s => parseFloat(s.trim()));
  const r = p[0], g = p[1], b = p[2], a = p.length > 3 ? p[3] : 1;
  const f = v => Math.round(v * a + 255 * (1 - a));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}
